<template>
  <div v-if="!result" class="calculation-steps">
    <h2 class="h4">
      How to Find the Coefficient of Variation of a Population: A Step-by-Step Guide
    </h2>
    <p class="bottom-margins">
      You can quickly calculate the CV of a population if you select Population in our Coefficient of Variation Calculator. Yet knowing how to do it by hand allows for a more in-depth understanding of the statistical method. To do so, follow these steps:
    </p>
    <ol>
      <li>
        <p>Calculate the population mean of the dataset:</p>
        <p>
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
      </li>
      <li>
        <p>Subtract the mean from each value in the dataset:</p>
        <p>
          \[(X_i-\mu)\]
        </p>
      </li>
      <li>
        <p>Calculate each observation’s squared deviation from the population mean:</p>
        <p>
          \[{(X_i-\mu)}^2\]
        </p>
      </li>
      <li>
        <p>Calculate the sum of all the squared differences to obtain the sum of squares (SS):</p>
        <p>
          \[SS=\sum_{i=1}^{n}{(X_i-\mu)}^2\]
        </p>
      </li>
      <li>
        <p>Divide the sum of squared differences by n to obtain the population variance (<span class="inline-formulas">\[\sigma^2\]</span>):</p>
        <p>
          \[\sigma^2=\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}\]
        </p>
      </li>
      <li>
        <p>Take the square root of the sum of the squared deviations to calculate the population standard deviation:</p>
        <p>
          \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}\]
        </p>
      </li>
      <li>
        <p>Calculate the coefficient of variation by dividing the population standard deviation by the sample mean:</p>
        <p class="default-last-formula">
          \[CV=\frac{s}{\bar{X}}\]
        </p>
      </li>
    </ol>

    <h2 class="h4 additional-h2">
      How to Find the Coefficient of Variation of a Sample: A Step-by-Step Guide
    </h2>
    <p class="bottom-margins">
      Statistical analysis software typically calculates the coefficient of variation automatically. You can also obtain it using our CV Calculator. But there are benefits to computing it manually as you get to know how the formula works:
    </p>
    <p class="bottom-margins">
      Follow these steps when calculating the <span class="bolded-texts">coefficient of variation</span> of a sample:
    </p>
    <ol>
      <li>
        <p>
          Calculate the sample mean of the dataset:
        </p>
        <p>
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
      </li>
      <li>
        <p>Subtract the mean from each value in the dataset:</p>
        <p>
          \[(X_i-\bar{X})\]
        </p>
      </li>
      <li>
        <p>Calculate each observation’s squared deviation from the sample mean:</p>
        <p>
          \[{(X_i-\bar{X})}^2\]
        </p>
      </li>
      <li>
        <p>Calculate the sum of all the squared differences to obtain the sum of squares (SS):</p>
        <p>
          \[SS=\sum_{i=1}^{n}{(X_i-\bar{X})}^2\]
        </p>
      </li>
      <li>
        <p>Divide the sum of squared differences by n – 1 to obtain the variance (<span class="inline-formulas">\[s^2\]</span>):</p>
        <p>
          \[s^2=\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}\]
        </p>
      </li>
      <li>
        <p>Take the square root of the sum of the squared deviations to calculate the sample standard deviation:</p>
        <p>
          \[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}\]
        </p>
      </li>
      <li>
        <p>Calculate the coefficient of variation by dividing the sample standard deviation by the sample mean:</p>
        <p class="default-last-formula">
          \[CV=\frac{\sigma}{\mu}\]
        </p>
      </li>
    </ol>
  </div>

  <div v-else :key="result" class="calculation-steps result">
    <h2 class="h4">
      Calculation Steps:
    </h2>
    <p class="bottom-margins">
      To calculate the coefficient of variation (CV) of a {{ calculationData.method }}, you can follow these steps:
    </p>
    <ol>
      <li>
        <p>
          Calculate the {{ calculationData.method }} mean of the dataset:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ calculationData.mean }}\]
        </p>
        <p v-else class="formulas">
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ calculationData.mean }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Subtract the mean from each value in the dataset. We have the following equation for the first variable:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[(X_i-\bar{X})={{ calculationData.rawDataMeanArray[0] }}-{{ calculationData.mean }}={{ calculationData.meanSubtractedFirstValue }}\]
        </p>
        <p v-else class="formulas">
          \[(X_i-\mu)={{ calculationData.rawDataMeanArray[0] }}-{{ calculationData.mean }}={{ calculationData.meanSubtractedFirstValue }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Calculate each observation’s squared deviation from the {{ calculationData.method }} mean. We have the following equation for the first variable:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[{(X_i-\bar{X})}^2={ {{ calculationData.meanSubtractedFirstValue }} }^2={{ calculationData.meanSubtractedSquaredFirstValue }}\]
        </p>
        <p v-else class="formulas">
          \[{(X_i-\mu)}^2={ {{ calculationData.meanSubtractedFirstValue }} }^2={{ calculationData.meanSubtractedSquaredFirstValue }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Calculate the sum of all the squared differences to obtain the sum of squares (SS):
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[\sum_{i=1}^{n}{\left(X_i-\bar{X}\right)^2=\sum_{i=1}^{n}{ {{ sqrString }} }={{ calculationData.sumOfSquares }}}\]
        </p>
        <p v-else class="formulas">
          \[\sum_{i=1}^{n}{\left(X_i-\mu\right)^2=\sum_{i=1}^{n}{ {{ sqrString }} }={{ calculationData.sumOfSquares }}}\]
        </p>
      </li>
      <li>
        <div v-if="calculationData.method === 'sample'" class="top-margins">
          <p>
            Divide the sum of squared differences by n – 1 to obtain the variance (<span class="inline-formulas">\[s^2\]</span>).
          </p>
          <p class="formulas">
            \[s^2=\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}=\frac{ {{ calculationData.sumOfSquares }} }{ {{ calculationData.size }}-1}={{ calculationData.variance }}\]
          </p>
        </div>
        <div v-else class="top-margins">
          <p>Divide the sum of squared differences by n to obtain the population variance (<span class="inline-formulas">\[\sigma^2\]</span>).</p>
          <p class="formulas">
            \[\sigma^2=\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}=\frac{ {{ calculationData.sumOfSquares }} }{ {{ calculationData.size }} }={{ calculationData.variance }}\]
          </p>
        </div>
      </li>
      <li>
        <p class="top-margins">
          Take the square root of the sum of the squared deviations to calculate the {{ calculationData.method }} standard deviation:
        </p>
        <p class="formulas">
          \[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}=\sqrt{ {{ calculationData.variance }} }={{ calculationData.standardDeviation }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Calculate the coefficient of variation by dividing the {{ calculationData.method }} standard deviation by the sample mean:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[CV=\frac{s}{\bar{X}}=\frac{ {{ calculationData.standardDeviation }} }{ {{ calculationData.mean }} }={{ calculationData.coefficientOfVariation }}\]
        </p>
        <p v-else class="formulas">
          \[CV=\frac{\sigma}{\mu}=\frac{ {{ calculationData.standardDeviation }} }{ {{ calculationData.mean }} }={{ calculationData.coefficientOfVariation }}\]
        </p>
      </li>
    </ol>

    <div v-if="calculationData.method === 'sample'">
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide sample coefficient of variation using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-sample">import statistics

# Define the sample data

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate the coefficient of variation using sample data

cv = statistics.stdev(data) / statistics.mean(data)

# Print the result

print("Coefficient of variation:", cv)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-sample')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide sample coefficient of variation  using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-sample"># Define the sample data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate the coefficient of variation using sample data

cv &lt;- sd(data) / mean(data)

# Print the result

print(paste("Coefficient of variation:", cv))</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-sample')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
    <div v-else>
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide the variance using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-population">import statistics

# Define the population data

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate the coefficient of variation using population data

cv = statistics.pstdev(data) / statistics.mean(data) * 100

# Print the result

print("Coefficient of variation:", cv)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-population')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide the variance using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-population"># Define the population data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate the population standard deviation

pop_std_dev &lt;- sqrt(var(data) * (length(data) - 1) / length(data))

# Calculate the coefficient of variation using population data

cv &lt;- pop_std_dev / mean(data)

# Print the result

print(paste("Population coefficient of variation:", cv))</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-population')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import calculatorsStepsMixin from '@itbpbg/lms-components/src/mixins/calculators/steps';

export default {
  mixins: [calculatorsStepsMixin],
  computed: {
    additionString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `${num} + ` : num));
      return rawDataMeanArray.join('');
    },
    sqrString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `(${num} - ${this.calculationData.mean})^2 + ` : `(${num} - ${this.calculationData.mean})^2`));
      return rawDataMeanArray.join('');
    },
    arrayToString() {
      return this.calculationData.rawDataMeanArray.join(', ');
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  p {
    margin: 0;
  }

  .python-r-codes.python {
    margin-top: var(--spacing-80);
  }

  ul {
    padding-left: 0;
  }

  ol {
    padding-left: 20px;
    list-style-position: outside;
  }

  .additional-h2 {
    margin-top: var(--spacing-40);
  }
}

@include bp($bp-mobile) {
  div .calculation-steps {
    padding-top: var(--spacing-40);

    .h4 {
      text-align: left;
    }
  }
}

@include bp($bp-phone) {
  .calculation-steps {
    .additional-h2 {
      margin-top: var(--spacing-80);
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
